/**
 * Page publique de signature — Avis de renouvellement de bail.
 *
 * Route : /sign/rent-increase/:token
 *
 * Accessible SANS auth (lien magique). Affiche le PDF + 2 boutons (Accepter/Refuser
 * pour locataire, ou Signer pour propriétaire selon le rôle déduit du token).
 */
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, FileText, Check, X, CheckCircle2, AlertCircle, Building2,
} from "lucide-react";
import PdfFrame from "@/components/shared/PdfFrame";
import { API } from "@/api";


export default function RentIncreaseSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [decision, setDecision] = useState(null); // "accepte" | "refuse"
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { message }
  const pdfBlobUrlRef = useRef(null);
  const [pdfReady, setPdfReady] = useState(false);

  // Load workflow info
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/rent-increase-sign/${token}`);
        if (cancelled) return;
        setInfo(r.data);
        // Pré-remplit avec le nom du signataire courant (basé sur le rôle)
        setName(r.data.signer_name || r.data.tenant_name || r.data.landlord_name || "");
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Lien invalide ou expiré");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Load PDF as blob (for inline preview)
  useEffect(() => {
    if (!info) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/rent-increase-sign/${token}/pdf`, { responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        pdfBlobUrlRef.current = url;
        setPdfReady(true);
      } catch {
        if (!cancelled) setPdfReady(false);
      }
    })();
    return () => {
      cancelled = true;
      if (pdfBlobUrlRef.current) URL.revokeObjectURL(pdfBlobUrlRef.current);
    };
  }, [info, token]);

  const submit = async () => {
    if (!name.trim()) {
      alert("Veuillez saisir votre nom complet pour signer.");
      return;
    }
    const isTenantRole = info.role === "tenant" || info.role === "tenant_2";
    if (isTenantRole && !decision) {
      alert("Veuillez choisir d'accepter ou refuser avant de signer.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/rent-increase-sign/${token}`, {
        name: name.trim(),
        decision: isTenantRole ? decision : undefined,
      });
      setDone(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur lors de la signature");
    } finally {
      setSubmitting(false);
    }
  };

  // ── États d'affichage ────────────────────────────────────────────────────
  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-800 mb-1">Lien inaccessible</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center py-10">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Merci !</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto">{done.message}</p>
          <p className="text-xs text-slate-400 mt-4">
            Une copie du document signé a été envoyée par courriel à toutes les parties concernées.
          </p>
        </div>
      </Shell>
    );
  }

  // Page déjà signée par cet utilisateur (mais workflow pas terminé), ou déjà completed
  if (!info.can_sign) {
    const statusLabels = {
      pending_tenants: "En attente de signature des locataires",
      pending_tenant: "En attente de signature du locataire",
      pending_landlord: "En attente de signature du propriétaire",
      completed: "Signatures complétées — avis archivé",
      cancelled: "Workflow annulé par l'administrateur",
    };
    const myResponse = info.role === "tenant"
      ? info.tenant_response
      : info.role === "tenant_2"
        ? info.tenant_2_response
        : info.landlord_response;
    return (
      <Shell>
        <div className="text-center py-10">
          <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-800 mb-1">Aucune action requise</h2>
          <p className="text-sm text-slate-500 mb-4">{statusLabels[info.status] || info.status}</p>
          <p className="text-xs text-slate-400">
            Votre signature{" "}
            {info.role === "landlord" ? "de propriétaire" : "de locataire"}{" "}
            {myResponse
              ? "a été enregistrée avec succès."
              : "n'est plus disponible (workflow terminé ou annulé)."}
          </p>
        </div>
        {pdfReady && <PdfPreview src={pdfBlobUrlRef.current} openUrl={`${API}/public/rent-increase-sign/${token}/pdf`} />}
      </Shell>
    );
  }

  const isTenant = info.role === "tenant" || info.role === "tenant_2";
  const otherTenantResponse = info.role === "tenant"
    ? info.tenant_2_response
    : info.role === "tenant_2"
      ? info.tenant_response
      : null;
  const otherTenantName = info.role === "tenant"
    ? info.tenant_2_name
    : info.role === "tenant_2"
      ? info.tenant_name
      : null;

  return (
    <Shell>
      {/* Bandeau résumé */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Référence</div>
        <div className="text-sm font-semibold text-slate-800 mb-3">{info.reference}</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div className="text-slate-500">Logement :</div>
          <div className="text-slate-800">
            {info.summary.property_address}
            {info.summary.unit_label ? ` — Unité ${info.summary.unit_label}` : ""}
          </div>
          <div className="text-slate-500">Loyer actuel :</div>
          <div className="text-slate-800">{Number(info.summary.current_rent).toFixed(2)} $</div>
          <div className="text-slate-500">Loyer proposé :</div>
          <div className="text-slate-800 font-semibold">{Number(info.summary.new_rent).toFixed(2)} $</div>
          <div className="text-slate-500">Entrée en vigueur :</div>
          <div className="text-slate-800">{info.summary.effective_date}</div>
        </div>
      </div>

      {/* PDF preview */}
      {pdfReady ? (
        <PdfPreview src={pdfBlobUrlRef.current} openUrl={`${API}/public/rent-increase-sign/${token}/pdf`} />
      ) : (
        <div className="border rounded-lg bg-slate-50 p-8 text-center text-slate-400 text-sm mb-5">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement du document…
        </div>
      )}

      {/* Zone de signature */}
      <div className="bg-white border-2 border-indigo-200 rounded-lg p-5 mt-5">
        <h3 className="font-semibold text-slate-900 mb-3">
          {isTenant ? "Votre réponse" : "Accusé de réception"}
        </h3>

        {isTenant && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => setDecision("accepte")}
              className={`px-4 py-3 rounded-lg border-2 text-sm font-semibold transition ${
                decision === "accepte"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid="rent-sign-accept"
            >
              <Check className="w-5 h-5 inline mr-1" /> J'accepte le renouvellement
            </button>
            <button
              onClick={() => setDecision("refuse")}
              className={`px-4 py-3 rounded-lg border-2 text-sm font-semibold transition ${
                decision === "refuse"
                  ? "border-rose-500 bg-rose-50 text-rose-700"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid="rent-sign-refuse"
            >
              <X className="w-5 h-5 inline mr-1" /> Je refuse le renouvellement
            </button>
          </div>
        )}

        {!isTenant && info.tenant_response && (
          <div className={`mb-4 p-3 rounded-lg border ${
            info.tenant_response.decision === "accepte"
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200"
          }`}>
            <div className="text-xs font-semibold mb-1 text-slate-600">
              Réponse du locataire{info.tenant_2_name ? " #1" : ""}
            </div>
            <div className="text-sm">
              <span className="font-semibold">{info.tenant_response.name}</span>{" "}
              a <span className="font-semibold">
                {info.tenant_response.decision === "accepte" ? "ACCEPTÉ" : "REFUSÉ"}
              </span> le renouvellement
            </div>
          </div>
        )}
        {!isTenant && info.tenant_2_response && (
          <div className={`mb-4 p-3 rounded-lg border ${
            info.tenant_2_response.decision === "accepte"
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200"
          }`}>
            <div className="text-xs font-semibold mb-1 text-slate-600">Réponse du locataire #2</div>
            <div className="text-sm">
              <span className="font-semibold">{info.tenant_2_response.name}</span>{" "}
              a <span className="font-semibold">
                {info.tenant_2_response.decision === "accepte" ? "ACCEPTÉ" : "REFUSÉ"}
              </span> le renouvellement
            </div>
          </div>
        )}

        {/* Co-signataire (autre locataire) : statut affiché au signataire courant */}
        {isTenant && otherTenantName && (
          <div className={`mb-4 p-3 rounded-lg border ${
            otherTenantResponse
              ? otherTenantResponse.decision === "accepte"
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"
              : "bg-slate-50 border-slate-200"
          }`}>
            <div className="text-xs font-semibold mb-1 text-slate-600">Co-locataire</div>
            <div className="text-sm">
              <span className="font-semibold">{otherTenantName}</span>{" "}
              {otherTenantResponse ? (
                <>a <span className="font-semibold">
                  {otherTenantResponse.decision === "accepte" ? "ACCEPTÉ" : "REFUSÉ"}
                </span> le renouvellement</>
              ) : (
                <span className="text-slate-500 italic">n'a pas encore signé</span>
              )}
            </div>
          </div>
        )}

        <label className="block text-xs font-semibold text-slate-600 mb-1">
          Votre nom complet (signature électronique)
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Prénom Nom"
          className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm font-serif italic text-indigo-700 focus:border-indigo-400 focus:outline-none"
          data-testid="rent-sign-name"
        />
        <p className="text-[10px] text-slate-400 mt-1">
          En cliquant sur "Signer", vous reconnaissez que votre nom saisi ci-dessus tient lieu de signature
          électronique légalement valide.
        </p>

        <button
          onClick={submit}
          disabled={submitting || !name.trim() || (isTenant && !decision)}
          className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="rent-sign-submit"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…</>
          ) : isTenant ? (
            <>Signer électroniquement</>
          ) : (
            <>Accuser de réception</>
          )}
        </button>
      </div>
    </Shell>
  );
}

function PdfPreview({ src, openUrl }) {
  return (
    <div className="w-full border rounded-lg shadow-sm overflow-hidden bg-white">
      <PdfFrame src={src} openUrl={openUrl} filename="avis-renouvellement.pdf" title="Avis de renouvellement" height="600px" label="Ouvrir l'avis (PDF)" testId="rent-sign-pdf-preview" />
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border p-5 sm:p-8">
        <div className="flex items-center gap-3 mb-5 pb-4 border-b">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="font-bold text-slate-900">Avis de renouvellement de bail</h1>
            <p className="text-xs text-slate-500">Signature électronique sécurisée — DG Comptabilité</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
